"use client";

import { useState } from "react";
import { inputClass, labelClass, primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { Segmented } from "@/components/Segmented";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { usePdfSummaries } from "@/hooks/usePdfSummaries";
import { useToolFiles } from "@/hooks/useToolFiles";
import { outputName } from "@/lib/files";
import {
  DPI_CHOICES,
  pagesToExport,
  renderScale,
  type Dpi,
} from "@/lib/pdf/to-image";
import { getTool } from "@/lib/tools";
import { renderPagesToImages } from "@/workers/pdf-render";

const tool = getTool("pdf-to-jpg")!;

const DPI_LABELS: Record<Dpi, string> = {
  72: "72 · screen",
  150: "150 · good",
  300: "300 · print",
};

/** FR-15: JPG or PNG, 72/150/300 DPI, all pages or a range; ZIP for several. */
export function PdfToImageTool() {
  const { files, setFiles, notices } = useToolFiles(tool);
  const file = files[0];
  const summary = usePdfSummaries(files).get(file);
  const info = summary && "pages" in summary ? summary : undefined;
  const [format, setFormat] = useState<"jpg" | "png">("jpg");
  const [dpi, setDpi] = useState<Dpi>(150);
  const [mode, setMode] = useState<"all" | "range">("all");
  const [range, setRange] = useState("1");
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState("");

  const plan = info ? pagesToExport(mode, range, info.pages) : null;
  const pages = plan && "pages" in plan ? plan.pages : [];
  const firstSize = info ? renderScale(info.size, dpi) : null;

  async function convert() {
    if (!file) return;
    setError("");
    setProgress({ done: 0, total: pages.length });
    try {
      const images = await renderPagesToImages(
        file,
        pages,
        dpi,
        format === "jpg" ? "image/jpeg" : "image/png",
        (done) => setProgress({ done, total: pages.length }),
      );
      setResults(
        images.map((img) => ({
          blob: img.blob,
          name: outputName(file.name, `page-${img.page + 1}`, format),
          detail: `${img.width} × ${img.height} px${img.reduced ? " · made smaller to fit phones" : ""}`,
        })),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProgress(null);
    }
  }

  const base = file ? file.name.replace(/\.pdf$/i, "") : "pdf";

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={(next) => {
        setFiles(next);
        setResults(null);
        setError("");
      }}
      notices={notices}
      results={results}
      zipName={`${base}-images.zip`}
      onStartOver={() => {
        setFiles([]);
        setResults(null);
      }}
      fileDetails={[
        info
          ? `${info.pages} ${info.pages === 1 ? "page" : "pages"}`
          : undefined,
      ]}
    >
      {summary && "error" in summary ? (
        <p role="alert" className="font-medium text-destructive">
          {summary.error}
        </p>
      ) : (
        <section
          aria-label="Settings"
          className="flex flex-col gap-5 rounded-[16px] border bg-card p-4 sm:p-5"
        >
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Save as</span>
            <Segmented
              label="Save as"
              stretch
              value={format}
              onChange={setFormat}
              options={[
                { value: "jpg", label: "JPG" },
                { value: "png", label: "PNG" },
              ]}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Resolution (DPI)</span>
            <Segmented
              label="Resolution"
              stretch
              value={String(dpi)}
              onChange={(v) => setDpi(Number(v) as Dpi)}
              options={DPI_CHOICES.map((d) => ({
                value: String(d),
                label: DPI_LABELS[d],
              }))}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Pages</span>
            <Segmented
              label="Pages"
              stretch
              value={mode}
              onChange={setMode}
              options={[
                { value: "all", label: "All pages" },
                { value: "range", label: "Choose pages" },
              ]}
            />
            {mode === "range" && (
              <label className={labelClass}>
                Pages, e.g. 1-3, 5
                <input
                  className={inputClass}
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                />
              </label>
            )}
          </div>

          <div className="rounded-lg bg-canvas p-3.5" aria-live="polite">
            {!info ? (
              <p className="text-muted-foreground">Reading your PDF…</p>
            ) : plan && "error" in plan ? (
              <p role="alert" className="font-medium text-destructive">
                {plan.error}
              </p>
            ) : (
              <>
                <p className="font-semibold" data-testid="export-summary">
                  {pages.length} {pages.length === 1 ? "image" : "images"} as{" "}
                  {format.toUpperCase()}
                </p>
                {firstSize && (
                  <p className="text-sm text-ink">
                    Page 1 becomes {firstSize.width} × {firstSize.height} px
                    {firstSize.reduced
                      ? " (made smaller so phones can open it)"
                      : ""}
                  </p>
                )}
              </>
            )}
          </div>

          {error && (
            <p role="alert" className="font-medium text-destructive">
              {error}
            </p>
          )}

          {progress ? (
            <ProgressBar
              label={`Making image ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`}
              value={progress.done}
              max={progress.total}
            />
          ) : (
            <button
              type="button"
              onClick={convert}
              disabled={pages.length === 0}
              className={primaryButtonClass}
            >
              Convert to {format.toUpperCase()}
            </button>
          )}
          <p className="-mt-2 text-center text-[13px] text-muted-foreground">
            {pages.length > 1 ? "Downloads as a ZIP · " : ""}nothing is uploaded
          </p>
        </section>
      )}
    </ToolShell>
  );
}
